import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Divider, Drawer } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import DrawerHeader from "../../../components/detail-drawer/DrawerHeader";
import type SnackbarState from "../../../types/ecommerce/SnackbarState";
import type { AddressSuggestionDto } from "../../../generated/models";
import { AddressMap } from "../../../features/storefront-feature";
import type { StorefrontAddress, AddressFormValues, DrawerMode } from "../types";
import { useAddressBookApi, useAddressBookOrgUnitId } from "../context";
import { useAddressLookupState } from "../hooks";
import AddressLookup from "./AddressLookup";
import AddressForm, { hasAddressFormErrors } from "./AddressForm";
import AddressGroupSelect from "./AddressGroupSelect";
import DefaultAddressToggle from "./DefaultAddressToggle";
import EditAddressDrawerFooter from "./EditAddressDrawerFooter";
import {
  ConfirmAddressOverrideDialog,
  ConfirmAddressValidationDialog,
} from "./dialogs";

/**
 * Resolves a display name for an address, preferring siteName over shipTo.
 */
function resolveDisplayName(address: StorefrontAddress): string {
  return address.siteName || address.shipTo || "Address";
}

export interface EditAddressDrawerProps {
  open: boolean;
  onClose: () => void;
  mode: DrawerMode;
  address?: StorefrontAddress;
  onSaved: (state: SnackbarState) => void;
  onSave?: (address: StorefrontAddress) => void;
  /** Extra styles for the Drawer root (e.g. raising z-index when opened inside a modal dialog). */
  sx?: SxProps<Theme>;
}

function buildInitialFormValues(address?: StorefrontAddress): AddressFormValues {
  return {
    streetAddress: address?.streetAddress ?? "",
    streetAddress2: address?.streetAddress2 ?? "",
    city: address?.city ?? "",
    state: address?.state ?? "",
    zip: address?.zip ?? "",
    country: address?.country ?? "",
    siteName: address?.siteName ?? "",
  };
}

export default function EditAddressDrawer({
  open,
  onClose,
  mode,
  address,
  onSaved,
  onSave,
  sx,
}: Readonly<EditAddressDrawerProps>) {
  const api = useAddressBookApi();
  const orgUnitId = useAddressBookOrgUnitId();
  const { data: availableGroups } = api.useGetOrgUnitAddressGroups(orgUnitId);
  const createMutation = api.useCreateStorefrontAddress(orgUnitId);
  const updateMutation = api.useUpdateStorefrontAddress(orgUnitId);
  const validateMutation = api.useValidateAddress();

  const [formValues, setFormValues] = useState<AddressFormValues>(
    buildInitialFormValues(address),
  );
  const [selectedGroups, setSelectedGroups] = useState<string[]>(
    address?.groups ?? [],
  );
  const [isDefault, setIsDefault] = useState(address?.isDefault ?? false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);

  const lookup = useAddressLookupState();
  const { resolveCoordinates, failCoordinates, reset: resetLookup } = lookup;

  // Coordinate input: only set on drawer open (edit) or suggestion selection
  // The fetchId forces a fresh query even if the address values are structurally identical to a prior lookup
  const [coordinateInput, setCoordinateInput] = useState<AddressFormValues | null>(null);
  const [fetchId, setFetchId] = useState(0);
  const coordinates = api.useAddressCoordinates(coordinateInput, fetchId);

  const triggerCoordinateLookup = useCallback((values: AddressFormValues) => {
    setCoordinateInput(values);
    setFetchId((prev) => prev + 1);
  }, []);

  // Sync coordinate query results into lookup state
  useEffect(() => {
    if (coordinates.isSuccess && coordinates.data?.latitude != null && coordinates.data?.longitude != null) {
      resolveCoordinates({
        latitude: coordinates.data.latitude,
        longitude: coordinates.data.longitude,
      });
    } else if (coordinates.isError) {
      failCoordinates("Unable to resolve map location");
    }
  }, [coordinates.data, coordinates.isError, coordinates.isSuccess, coordinates.dataUpdatedAt, resolveCoordinates, failCoordinates]);

  // Reset form when address changes (drawer re-opens with different row)
  const resetForm = useCallback(
    (addr?: StorefrontAddress) => {
      setFormValues(buildInitialFormValues(addr));
      setSelectedGroups(addr?.groups ?? []);
      setIsDefault(addr?.isDefault ?? false);
      setError(null);
      setIsSaving(false);
      setShowValidationErrors(false);
      setValidationDialogOpen(false);
      resetLookup();
    },
    [resetLookup],
  );

  // Re-initialize when drawer opens with new address
  const handleTransitionEnter = useCallback(() => {
    resetForm(address);
    if (address) {
      triggerCoordinateLookup(buildInitialFormValues(address));
    } else {
      setCoordinateInput(null);
    }
  }, [address, resetForm, triggerCoordinateLookup]);

  const isReadOnly = mode === "read-only";

  const handleSuggestionSelected = (
    suggestion: AddressSuggestionDto,
    source: "autocomplete" | "street-lookup",
  ) => {
    const newFormValues = lookup.applySuggestion(suggestion, source);
    setFormValues((prev) => ({ ...prev, ...newFormValues, siteName: prev.siteName }));
    triggerCoordinateLookup(newFormValues);
  };

  const handleFormChange = (values: AddressFormValues) => {
    setFormValues(values);
    lookup.notifyFormChanged(values);
    // coordinateInput is intentionally NOT updated here — map stays static
  };

  const handleGroupsChange = useCallback(
    (groups: string[]) => {
      setSelectedGroups(groups);
    },
    [],
  );

  const handleSave = async () => {
    setShowValidationErrors(true);
    if (hasAddressFormErrors(formValues)) return;

    if (lookup.shouldPromptOverride) {
      lookup.openOverrideDialog();
      return;
    }

    if (!lookup.state.appliedSuggestion) {
      try {
        const result = await validateMutation.mutateAsync(formValues);
        if (!result.wasExactMatch) {
          setValidationDialogOpen(true);
          return;
        }
      } catch {
        setValidationDialogOpen(true);
        return;
      }
    }

    await performSave();
  };

  const handleValidationConfirm = async () => {
    setValidationDialogOpen(false);
    await performSave();
  };

  const performSave = async () => {
    setIsSaving(true);
    setError(null);

    const addressName = formValues.siteName.trim() || formValues.streetAddress.trim();

    try {
      let savedAddressId = address?.id ?? "";

      if (mode === "create") {
        const created = await createMutation.mutateAsync({
          address1: formValues.streetAddress.trim(),
          address2: formValues.streetAddress2.trim() || undefined,
          city: formValues.city.trim(),
          state: formValues.state.trim().toUpperCase(),
          postalCode: formValues.zip.trim(),
          addressName,
          isDefault: isDefault,
          addressGroupNames: selectedGroups,
        });
        const streetAddressId = Number(created.streetAddressId);
        if (!Number.isFinite(streetAddressId) || streetAddressId <= 0) {
          setError("The address was created but could not be selected. Please refresh and try again.");
          return;
        }
        savedAddressId = String(streetAddressId);
      } else {
        await updateMutation.mutateAsync({
          streetAddressId: Number(address?.id ?? 0),
          address1: formValues.streetAddress.trim(),
          address2: formValues.streetAddress2.trim() || undefined,
          city: formValues.city.trim(),
          state: formValues.state.trim().toUpperCase(),
          postalCode: formValues.zip.trim(),
          addressName,
          isDefault: isDefault,
          addressGroupNames: selectedGroups,
        });
      }

      onSave?.({
        id: savedAddressId,
        siteName: addressName,
        streetAddress: formValues.streetAddress.trim(),
        streetAddress2: formValues.streetAddress2.trim() || undefined,
        city: formValues.city.trim(),
        state: formValues.state.trim().toUpperCase(),
        zip: formValues.zip.trim(),
        country: formValues.country.trim() || "US",
        groups: selectedGroups,
        isDefault,
      });
      onSaved({
        open: true,
        message:
          mode === "create"
            ? "Address created successfully"
            : "Address updated successfully",
        severity: "success",
      });
      onClose();
    } catch {
      setError("Failed to save address. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleOverrideConfirm = () => {
    lookup.closeOverrideDialog();
    performSave();
  };

  const headerText =
    mode === "create"
      ? "New Address"
      : address
        ? resolveDisplayName(address)
        : "Address";

  const subheaderText =
    mode === "create"
      ? undefined
      : address
        ? `${address.streetAddress} · ${address.city}, ${address.state} ${address.zip}`
        : undefined;

  const mapLatitude = lookup.state.coordinates?.latitude;
  const mapLongitude = lookup.state.coordinates?.longitude;
  const addressMap = api.useAddressMap(mapLatitude, mapLongitude);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      role="dialog"
      aria-label="Edit address"
      sx={sx}
      slotProps={{
        paper: {
          sx: {
            width: { xs: "100vw", sm: 480 },
            maxWidth: "100vw",
            display: "flex",
            flexDirection: "column",
            height: "100vh",
          },
        },
      }}
      SlideProps={{ onEnter: handleTransitionEnter }}
    >
      <DrawerHeader
        icon="LocationOnOutlined"
        header={headerText}
        subheader={subheaderText}
        onClose={onClose}
      />

      <Divider />

      <Box sx={{ flex: 1, overflow: "auto", height: "100%" }}>
        <AddressMap
          mapData={addressMap.data}
          isLoading={addressMap.isInitialLoading}
          isError={addressMap.isError}
        />

        {mode === "create" && (
          <AddressLookup
            onSuggestionSelected={(s) => handleSuggestionSelected(s, "autocomplete")}
          />
        )}

        <AddressForm
          values={formValues}
          onChange={handleFormChange}
          disabled={isReadOnly}
          showErrors={showValidationErrors}
          onSuggestionSelected={(s) => handleSuggestionSelected(s, "street-lookup")}
        />

        <AddressGroupSelect
          selectedGroups={selectedGroups}
          onChange={handleGroupsChange}
          disabled={isReadOnly}
          availableGroups={availableGroups}
        />

        <DefaultAddressToggle
          checked={isDefault}
          onChange={setIsDefault}
          disabled={isReadOnly}
        />

        {(error || lookup.state.lookupError) && (
          <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
            {error || lookup.state.lookupError}
          </Alert>
        )}
      </Box>

      <EditAddressDrawerFooter
        mode={mode}
        isSaving={isSaving}
        onSave={handleSave}
        onCancel={onClose}
      />

      <ConfirmAddressOverrideDialog
        open={lookup.state.overrideDialogOpen}
        onConfirm={handleOverrideConfirm}
        onCancel={() => lookup.closeOverrideDialog()}
      />

      <ConfirmAddressValidationDialog
        open={validationDialogOpen}
        onConfirm={handleValidationConfirm}
        onCancel={() => setValidationDialogOpen(false)}
      />
    </Drawer>
  );
}
